import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineSquares2X2, HiOutlinePhoto, HiOutlineCalendar, HiOutlinePaintBrush, HiOutlineSwatch, HiOutlineTrophy, HiOutlineBuildingLibrary, HiOutlineMagnifyingGlass } from 'react-icons/hi2';
import { PhotoIcon } from '@heroicons/react/24/outline';
import AlertDialog from '../../common/AlertDialog';
import { useAdmin } from '../context/AdminContext';
import { PositionControls } from './PositionControls';

const API_URL = import.meta.env.VITE_API_URL;

export function PaintingList({ 
  paintings: propPaintings,
  series: propSeries,
  colors: propColors,
  awards: propAwards,
  exhibitions: propExhibitions,
  onEdit, 
  onDelete,
  paintingViews = {}
}) {
  const { t } = useTranslation();
  const { handlePositionUpdate } = useAdmin();
  
  // Use props if provided, fallback to AdminContext
  const { paintings: contextPaintings, series: contextSeries, colors: contextColors, awards: contextAwards, exhibitions: contextExhibitions } = useAdmin();
  const paintings = propPaintings || contextPaintings || [];
  const series = propSeries || contextSeries || [];
  const colors = propColors || contextColors || [];
  const awards = propAwards || contextAwards || [];
  const exhibitions = propExhibitions || contextExhibitions || [];
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(50);
  const [deleteId, setDeleteId] = useState(null);
  const [sortMode, setSortMode] = useState(false);
  const [positionInputs, setPositionInputs] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  // Hilfsfunktion für Serie-Name
  const getSerieName = (serieId) => {
    const serie = series.find(s => s.id === Number(serieId));
    return serie ? serie.name : '';
  };

  // Filterlogik
  const filteredPaintings = useMemo(() => {
    if (!Array.isArray(paintings)) return [];
    const q = search.trim().toLowerCase();
    if (!q) return paintings;
    return paintings.filter(p => {
      const serieName = getSerieName(p.serie_id).toLowerCase();
      return (
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.year && String(p.year).includes(q)) ||
        (serieName && serieName.includes(q)) ||
        (p.description && p.description.toLowerCase().includes(q))
      );
    });
  }, [paintings, search, series]);

  // Sichtbare Gemälde für Paging
  const visiblePaintings = filteredPaintings.slice(0, visibleCount);
  const hasMore = filteredPaintings.length > visibleCount;

  if (!Array.isArray(paintings) || paintings.length === 0) {
    return (
      <div className="text-center text-gray-500 dark:text-gray-400 py-8">
        {t('paintinglist.no_paintings', 'Noch keine Gemälde vorhanden.')}
      </div>
    );
  }

  // Handler für das Löschen mit Dialog
  const handleDeleteRequest = (id) => setDeleteId(id);
  const handleCancelDelete = () => setDeleteId(null);
  const handleConfirmDelete = () => {
    if (deleteId) {
      onDelete(deleteId);
      setDeleteId(null);
    }
  };

  // Für Dialog: Gemälde-Name
  const paintingToDelete = visiblePaintings.find(p => p.id === deleteId) || paintings.find(p => p.id === deleteId);

  // Position Update Handler - using AdminContext
  const handlePositionUpdateWrapper = async (paintingId, newPosition) => {
    if (isUpdating) return; // Prevent multiple simultaneous updates
    
    setIsUpdating(true);
    
    try {
      await handlePositionUpdate(paintingId, newPosition);
    } catch (error) {
      alert(t('paintinglist.position_error', 'Fehler beim Aktualisieren der Position'));
    } finally {
      setIsUpdating(false);
    }
  };

  // Quick position change handlers
  const moveToTop = (painting) => handlePositionUpdateWrapper(painting.id, 1);
  const moveUp = (painting) => handlePositionUpdateWrapper(painting.id, Math.max(1, painting.sort_order - 1));
  const moveDown = (painting) => handlePositionUpdateWrapper(painting.id, painting.sort_order + 1);
  const moveToBottom = (painting) => handlePositionUpdateWrapper(painting.id, paintings.length);

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Header mit Suchleiste und Sortier-Toggle */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        {/* Suchleiste */}
        <div className="flex-1 flex items-center gap-2 bg-white dark:bg-zinc-800 rounded-lg shadow px-3 py-2 border border-gray-200 dark:border-zinc-700">
          <HiOutlineMagnifyingGlass className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            className="flex-1 bg-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm"
            placeholder={t('paintinglist.search_placeholder', 'Suchen nach Titel, Jahr, Serie, Beschreibung...')}
            value={search}
            onChange={e => { setSearch(e.target.value); setVisibleCount(50); }}
          />
        </div>
        {/* Sortier-Modus Toggle */}
        <button
          onClick={() => setSortMode(!sortMode)}
          disabled={isUpdating}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            sortMode 
              ? 'bg-blue-600 text-white shadow-lg' 
              : 'bg-white dark:bg-zinc-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-zinc-700 shadow'
          }`}
        >
          {isUpdating && sortMode && <span className="mr-2">⟲</span>}
          {sortMode ? t('paintinglist.sort_mode_on', 'Sortier-Modus aktiv') : t('paintinglist.sort_mode_off', 'Sortier-Modus')}
        </button>
      </div>

      {/* Grid Layout */}
      {visiblePaintings.length === 0 ? (
        <div className="text-center text-gray-500 dark:text-gray-400 py-8">{t('paintinglist.no_results', 'Keine Gemälde gefunden.')}</div>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
          {visiblePaintings.map(p => {
            const mainImg = Array.isArray(p.images)
              ? p.images.find(img => img.is_main === 1 || img.is_main === '1' || img.is_main === true) || p.images[0]
              : null;
            const serie = series.find(s => s.id === Number(p.serie_id));
            const colorObjs = Array.isArray(p.color_ids)
              ? p.color_ids.map(cid => colors.find(c => c.id === cid)).filter(Boolean)
              : [];
            const isSold = !!p.sold;

            return (
              <div key={p.id} className="flex flex-col h-full">
                {/* Main Card */}
                <div className="group relative bg-white dark:bg-zinc-800 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden border border-gray-100 dark:border-zinc-700 hover:border-blue-200 dark:hover:border-blue-700 flex flex-col h-full">
                  {/* Position Badge (only in sort mode) */}
                  {sortMode && (
                    <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full z-10">
                      #{p.sort_order}
                    </div>
                  )}
                
                  {/* Image Section */}
                  <div className="relative w-full h-32 md:h-40 lg:h-48 bg-gray-100 dark:bg-zinc-900 flex items-center justify-center flex-shrink-0">
                    {mainImg ? (
                      <img
                        src={`${API_URL}${mainImg.thumb_path || mainImg.file_path}`}
                        alt={p.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-zinc-600">
                        <PhotoIcon className="w-12 h-12" />
                      </div>
                    )}
                    
                    {/* Featured-Stern */}
                    {p.featured && (
                      <div className="absolute top-3 left-3 z-10" title={t('paintingform.featured', 'Ausgewähltes Werk')}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="#facc15" viewBox="0 0 24 24" className="w-6 h-6 drop-shadow-lg">
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                        </svg>
                      </div>
                    )}
                    
                    {/* Verkauft-Badge */}
                    {isSold && (
                      <div className="absolute bottom-3 right-3 z-10">
                        <span className="bg-red-600 text-white px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
                          {t('paintinglist.sold', 'Verkauft')}
                        </span>
                      </div>
                    )}
                    
                    {/* Klicks/Views-Badge - immer links unten */}
                    {typeof paintingViews[p.id] === 'number' && (
                      <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1 bg-white/80 dark:bg-zinc-900/80 px-2 py-1 rounded-full text-xs font-semibold text-gray-700 dark:text-gray-200 shadow border border-gray-200 dark:border-zinc-700" title={t('paintinglist.views', 'Klicks/Views')}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-3 h-3 text-blue-500"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        {paintingViews[p.id]}
                      </div>
                    )}
                  </div>

                  {/* Content Section */}
                  <div className="p-2 md:p-3 flex flex-col gap-2 flex-grow">
                    {/* Title */}
                    <h3 className="font-semibold text-sm md:text-base text-gray-900 dark:text-white truncate leading-tight" title={p.name}>
                      {p.name}
                    </h3>
                    
                    {/* Price and Year/Size */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm md:text-base font-bold text-blue-600 dark:text-blue-400">
                        {p.price}€
                      </div>
                      <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400 truncate">
                        {p.year}{p.size && ` • ${p.size}`}
                      </div>
                    </div>

                    {/* Serie and Meta Row */}
                    <div className="flex items-center justify-between gap-2 min-h-[20px]">
                      {/* Serie Badge */}
                      {serie && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 rounded-full text-xs font-medium truncate max-w-[60%]" title={serie.name}>
                          <HiOutlineSquares2X2 className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{serie.name}</span>
                        </span>
                      )}
                      
                      {/* Awards/Exhibitions Badges */}
                      <div className="flex gap-1 flex-shrink-0">
                        {Array.isArray(p.award_ids) && p.award_ids.length > 0 && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 rounded-full text-xs font-medium">
                            <HiOutlineTrophy className="w-3 h-3" />
                            {p.award_ids.length}
                          </span>
                        )}
                        {Array.isArray(p.exhibition_ids) && p.exhibition_ids.length > 0 && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 rounded-full text-xs font-medium">
                            <HiOutlineBuildingLibrary className="w-3 h-3" />
                            {p.exhibition_ids.length}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Colors */}
                    {colorObjs.length > 0 && (
                      <div className="flex items-center gap-1 min-h-[16px]">
                        {colorObjs.slice(0, 4).map(c => (
                          <span
                            key={c.id}
                            className="w-3 h-3 md:w-4 md:h-4 rounded-full border border-white dark:border-zinc-700 shadow-sm flex-shrink-0"
                            style={{ background: c.color_code }}
                            title={c.name_de + ' / ' + c.name_en}
                          />
                        ))}
                        {colorObjs.length > 4 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">+{colorObjs.length - 4}</span>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-1 md:gap-2 mt-auto pt-1">
                      <button
                        className="flex-1 px-2 md:px-3 py-1 md:py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs md:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onClick={() => onEdit(p.id)}
                      >
                        Edit
                      </button>
                      <button
                        className="flex-1 px-2 md:px-3 py-1 md:py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs md:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                        onClick={() => handleDeleteRequest(p.id)}
                      >
                        Del
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Position Controls */}
                <PositionControls
                  item={p}
                  totalItems={paintings.length}
                  positionInputs={positionInputs}
                  setPositionInputs={setPositionInputs}
                  onPositionUpdate={handlePositionUpdateWrapper}
                  isUpdating={isUpdating}
                  sortMode={sortMode}
                />
              </div>
            );
          })}
        </div>
      )}
      
      {/* Load More Button */}
      {hasMore && (
        <button
          className="mt-4 mx-auto px-6 py-2 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 rounded-full shadow hover:bg-blue-200 dark:hover:bg-blue-800 transition-all"
          onClick={() => setVisibleCount(c => c + 50)}
        >
          {t('paintinglist.load_more', 'Mehr laden')}
        </button>
      )}
      
      {/* AlertDialog für Löschen */}
      <AlertDialog
        open={!!deleteId}
        title={t('paintinglist.delete_title', 'Gemälde löschen?')}
        description={paintingToDelete ? t('paintinglist.delete_confirm', { name: paintingToDelete.name, defaultValue: `Möchtest du das Gemälde „${paintingToDelete.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.` }) : t('paintinglist.delete_confirm_generic', 'Möchtest du dieses Gemälde wirklich löschen?')}
        confirmText={t('paintinglist.delete', 'Löschen')}
        cancelText={t('paintinglist.cancel', 'Abbrechen')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmColor="red"
      />
    </div>
  );
} 